import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AccusedRecord } from '../../../types/criminalCaseRequest';
import type { LookupItem } from '../../../mocks/criminalMasterData';
import FormattedDateRangeInput from '../../../components/ui/FormattedDateRangeInput';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { useAttorneyGeneralWizardContext } from '../../../hooks/useCaseRequestWizard';
import { newAttorneyGeneralAccused } from '../../../types/attorneyGeneralCaseRequest';
import { exceedsMax, isFutureIsoDate, todayIsoDateLocal } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import { AttorneyGeneralWizardActions } from './AttorneyGeneralWizardActions';
import { attorneyGeneralWizardPath, AttorneyGeneralWizardShell, FieldError, inpClass } from './AttorneyGeneralWizardShell';
import { isStatusOther } from './attorneyGeneralHelpers';
import { agEnterPlaceholder, agSelectPlaceholder } from './attorneyGeneralPlaceholders';
import { validateAttorneyGeneralStep } from './attorneyGeneralStepValidation';
import { useAttorneyGeneralLookups } from './useAttorneyGeneralLookups';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_TEXT = 2000;

function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

export default function AttorneyGeneralStep5OffenceAccusedPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useAttorneyGeneralWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const { suspectStatuses } = useAttorneyGeneralLookups('step5');
  const maxOffenceDate = useMemo(() => todayIsoDateLocal(), []);

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateAttorneyGeneralStep(5, draft, { suspectStatuses });
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const updateAccused = (id: string, patch: Partial<AccusedRecord>) => {
    setDraft((d) => ({ ...d, accused: d.accused.map((a) => (a.id === id ? { ...a, ...patch } : a)) }));
  };

  const addAccused = () => setDraft((d) => ({ ...d, accused: [...d.accused, newAttorneyGeneralAccused()] }));
  const removeAccused = (id: string) =>
    setDraft((d) => ({ ...d, accused: d.accused.length > 1 ? d.accused.filter((a) => a.id !== id) : d.accused }));

  return (
    <AttorneyGeneralWizardShell currentStep={5} draft={draft}>
      <SectionCard title={f.sectionOffenceDetails}>
        <div className="ccr-fg ccr-field-half" style={{ marginBottom: 14 }}>
          <label>{f.dateOfOffence}</label>
          <FormattedDateRangeInput
            id="ag-offenceFrom"
            className={inpClass(Boolean(errors.offenceDateFrom))}
            from={draft.offenceDateFrom}
            to={draft.offenceDateTo}
            maxDate={maxOffenceDate}
            enableYmdViews
            placeholder={agEnterPlaceholder('Date of Offence')}
            onChange={(from, to) => {
              setDraft((d) => ({ ...d, offenceDateFrom: from, offenceDateTo: to }));
              clearErr('offenceDateFrom');
              if (from && isFutureIsoDate(from)) {
                setErrors((e) => ({ ...e, offenceDateFrom: 'Date of Offence cannot be in the future.' }));
              }
            }}
          />
          <FieldError message={errors.offenceDateFrom} />
        </div>

        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>{f.placeOfOffence}</label>
          <textarea
            id="ag-placeOfOffence"
            className={inpClass(Boolean(errors.placeOfOffence))}
            rows={3}
            value={draft.placeOfOffence}
            onChange={(e) => setDraft((d) => ({ ...d, placeOfOffence: maxLen('placeOfOffence', e.target.value, MAX_TEXT) }))}
            placeholder={agEnterPlaceholder('Place of Offence')}
          />
          <FieldError message={errors.placeOfOffence} />
        </div>
      </SectionCard>

      <div className="ccr-accused-panel">
        <div className="ccr-accused-panel-hdr">Accused / Suspects</div>
        <div className="ccr-accused-panel-body">
          {draft.accused.map((a, i) => (
            <div key={a.id} style={{ marginBottom: i < draft.accused.length - 1 ? 12 : 0 }}>
              <div className="ccr-accused-row">
                <div className="ccr-fg">
                  <label>Accused / Suspect Name</label>
                  <input
                    id={`ag-accused-name-${i}`}
                    className={inpClass(Boolean(errors[`accused-name-${i}`]))}
                    type="text"
                    value={a.name}
                    onChange={(e) => {
                      const value = maxLen(`accused-name-${i}`, e.target.value, MAX_TEXT);
                      updateAccused(a.id, { name: value });
                    }}
                    placeholder={agEnterPlaceholder('Accused / Suspect Name')}
                  />
                  <FieldError message={errors[`accused-name-${i}`]} />
                </div>
                <div className="ccr-fg">
                  <label>Accused / Suspect Status</label>
                  <WizardSelect
                    id={`ag-accused-status-${i}`}
                    hasError={Boolean(errors[`accused-status-${i}`])}
                    value={a.status}
                    placeholder={agSelectPlaceholder('Accused / Suspect Status')}
                    options={suspectStatuses.map((s: LookupItem) => ({ id: s.id, label: s.name }))}
                    onChange={(id) => updateAccused(a.id, { status: id, statusOther: '' })}
                  />
                  <FieldError message={errors[`accused-status-${i}`]} />
                </div>
                <button
                  type="button"
                  className="ccr-accused-del"
                  aria-label={`Remove accused ${i + 1}`}
                  disabled={draft.accused.length <= 1}
                  onClick={() => removeAccused(a.id)}
                >
                  <TrashIcon />
                </button>
              </div>
              {isStatusOther(a.status, suspectStatuses) ? (
                <div className="ccr-accused-other ccr-fg">
                  <label>
                    Status (Other) <span className="req">*</span>
                  </label>
                  <input
                    id={`ag-accused-statusOther-${i}`}
                    className={inpClass(Boolean(errors[`accused-statusOther-${i}`]))}
                    type="text"
                placeholder={agEnterPlaceholder('Status (Other)')}
                    value={a.statusOther}
                    onChange={(e) => {
                      const value = maxLen(`accused-statusOther-${i}`, e.target.value, 255);
                      updateAccused(a.id, { statusOther: value });
                    }}
                  />
                  <FieldError message={errors[`accused-statusOther-${i}`]} />
                </div>
              ) : null}
            </div>
          ))}

          <button className="ccr-btn-ol" type="button" style={{ marginTop: 14 }} onClick={addAccused}>
            + Add Accused/Suspect
          </button>
        </div>
      </div>

      <AttorneyGeneralWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={5}
        onBack={() => nav(attorneyGeneralWizardPath(4, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(attorneyGeneralWizardPath(6, draft.draftRequestId));
        }}
        validateBeforeSave={validate}
      />
    </AttorneyGeneralWizardShell>
  );
}
